import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { ResolveService } from 'src/app/services/resolve.service';
import { AuthState } from 'src/app/store/auth/auth.state';
import { User, UserRole } from 'src/models';

type FlatUser = {
  id: string;
  name?: string;
} | {
  userId: string;
  userName?: string;
};

@UntilDestroy()
@Component({
  selector: 'app-user',
  templateUrl: './component.html',
  styleUrls: ['./component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserComponent {
  user$ = new BehaviorSubject<User | FlatUser>(null);
  @Input() set user(user: User | FlatUser) {
    this.user$.next(user);
  }
  get user(): User | FlatUser {
    return this.user$.value;
  }

  @Input() hideAvatar = false;
  @Input() hideLink = false;
  @Input() size = 24;
  @Input() textSizeRatio = 32 / 14;
  @Input() resolve = false;

  @Select(AuthState.role) role$!: Observable<UserRole>;

  get userId(): string {
    if (!this.user) return null;
    if ('id' in this.user) return this.user.id;
    if ('userId' in this.user) return this.user.userId;
    return null;
  }

  get userName(): string {
    if (!this.user) return null;
    if ('name' in this.user) return this.user.name;
    if ('userName' in this.user) return this.user.userName;
    return null;
  }

  constructor(private resolveService: ResolveService) { }

  resolvedUserName$ = this.user$.pipe(
    switchMap((user) => this.userName ? of(this.userName) : (!this.resolve ? of('') : this.resolveService.resolveUser(this.userId).then(u => u?.name))),
  );
}
